// Client helpers for the targeted per-application notifications feature. Backed
// by t5/v1/notifications (cres-aws-t5 handlers/cres/v1/notifications.py):
//   GET  -> notifications applicable to the current user (+ per-user read state)
//   PUT  -> mark one ({USID}) or all ({All:true}) read
// The bell (NavBar) renders these; each notification's `notificationType` tells
// the UI what to do on click (see notificationTarget()).
import CresAxios from "../util/CresAxios";

// The suite application this front-end represents. Sent to the notifications
// endpoint so the bell shows notifications intended for this app or for all apps.
export const CURRENT_APPLICATION = "DataCatalog";

export type AppNotification = {
    usid: string;
    /** Apps this notification is intended for (subset of QMS/Developer/
     *  SBOM/DataCatalog); null means ALL applications. */
    applications: string[] | null;
    /** App-specific type the UI dispatches on (what to do when clicked). */
    notificationType: string;
    title: string;
    body: string;
    /** Parsed action-context JSON (may carry a `url` and type-specific fields). */
    data: any;
    /** Convenience deep link (data.url), when present. */
    url: string;
    read: boolean;
    createdDatetime: string;
};

export type NotificationsResult = {
    notifications: AppNotification[];
    unreadCount: number;
};

function toNotification(raw: any): AppNotification {
    const data = raw?.Data ?? null;
    const url = data && typeof data === "object" && data.url ? String(data.url) : "";
    return {
        usid: raw?.USID ?? "",
        applications: Array.isArray(raw?.Applications) ? raw.Applications : null,
        notificationType: raw?.NotificationType ?? "",
        title: raw?.Title ?? "",
        body: raw?.Body ?? "",
        data,
        url,
        read: !!raw?.Read,
        createdDatetime: raw?.CreatedDatetime ?? "",
    };
}

/** Notifications for the current user, newest first, plus the unread count.
 *  Scoped to this app (and all-app notifications) unless `application` is "". */
export async function fetchNotifications(application: string = CURRENT_APPLICATION): Promise<NotificationsResult> {
    const res = await CresAxios({
        method: "GET",
        url: `${process.env.REACT_APP_CATALOG_API_URL}/t5/v1/notifications`,
        params: application ? {Application: application} : undefined,
    });
    const body = res?.data ?? {};
    const list = Array.isArray(body.Notifications) ? body.Notifications : [];
    return {
        notifications: list.map(toNotification),
        unreadCount: Number(body.UnreadCount) || 0,
    };
}

/** Mark a single notification read for the current user. */
export async function markNotificationRead(usid: string): Promise<void> {
    if (!usid) return;
    await CresAxios({method: "PUT", url: `${process.env.REACT_APP_CATALOG_API_URL}/t5/v1/notifications`, data: {USID: usid}});
}

/** Mark every applicable notification read for the current user. */
export async function markAllNotificationsRead(): Promise<void> {
    await CresAxios({method: "PUT", url: `${process.env.REACT_APP_CATALOG_API_URL}/t5/v1/notifications`, data: {All: true}});
}

/**
 * Where clicking a notification should take the user, dispatched by
 * notificationType (falling back to a `url` in its data). Returning "" means no
 * navigation. Extend this as new notification types are added.
 */
export function notificationTarget(n: AppNotification): string {
    switch (n.notificationType) {
        case "community.watched-tag.new-question":
            return n.url || (n.data?.slug ? `/community/parkouranswers/q/${n.data.slug}` : "");
        default:
            return n.url || "";
    }
}
